import React, { useEffect, useState, useCallback } from 'react';

const isArcade = () =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('arcade-max-page');

// 8-bit burst: a square ring stepping outward plus four blocks flying off the
// diagonals. Matches the pixel cursor, so pressing feels like the same world.
const PixelBurst = ({ x, y }) => (
  <div className="a-burst" style={{ left: x, top: y }}>
    <span className="a-burst-ring" />
    {[45, 135, 225, 315].map((deg) => (
      <span key={deg} className="a-burst-block" style={{ '--deg': `${deg}deg` }} />
    ))}
  </div>
);

const CrosshairBurst = ({ x, y }) => (
  <div
    className="pointer-events-none fixed z-[9999]"
    style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
  >
    {/* Crosshair lines */}
    <svg width="40" height="40" viewBox="0 0 40 40" className="click-burst-svg">
      <line x1="20" y1="4" x2="20" y2="16" stroke="hsl(var(--primary))" strokeWidth="1.5" className="burst-line" />
      <line x1="20" y1="24" x2="20" y2="36" stroke="hsl(var(--primary))" strokeWidth="1.5" className="burst-line" />
      <line x1="4" y1="20" x2="16" y2="20" stroke="hsl(var(--primary))" strokeWidth="1.5" className="burst-line" />
      <line x1="24" y1="20" x2="36" y2="20" stroke="hsl(var(--primary))" strokeWidth="1.5" className="burst-line" />
      <circle cx="20" cy="20" r="6" fill="none" stroke="hsl(var(--primary))" strokeWidth="1" className="burst-ring" />
    </svg>
    {/* Corner sparks */}
    {[45, 135, 225, 315].map((deg) => (
      <div
        key={deg}
        className="burst-spark"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: 2,
          height: 2,
          borderRadius: '50%',
          background: 'hsl(var(--primary))',
          transform: `rotate(${deg}deg) translateY(-8px)`,
          transformOrigin: '0 0',
        }}
      />
    ))}
  </div>
);

const ClickBurst = ({ x, y, id, arcade, onDone }) => {
  useEffect(() => {
    const t = setTimeout(() => onDone(id), arcade ? 420 : 600);
    return () => clearTimeout(t);
  }, [id, arcade, onDone]);

  return arcade ? <PixelBurst x={x} y={y} /> : <CrosshairBurst x={x} y={y} />;
};

export const ClickEffect = () => {
  const [bursts, setBursts] = useState([]);

  const remove = useCallback((id) => {
    setBursts((prev) => prev.filter((b) => b.id !== id));
  }, []);

  useEffect(() => {
    let counter = 0;
    const handler = (e) => {
      counter++;
      setBursts((prev) => [...prev, { id: counter, x: e.clientX, y: e.clientY, arcade: isArcade() }]);
    };
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  // Drives the pressed-state cursor. Kept here so there's one place listening
  // for clicks rather than a second global listener elsewhere.
  useEffect(() => {
    const root = document.documentElement;
    const down = () => root.classList.add('arcade-pressed');
    const up = () => root.classList.remove('arcade-pressed');
    window.addEventListener('mousedown', down);
    window.addEventListener('mouseup', up);
    window.addEventListener('blur', up);
    return () => {
      window.removeEventListener('mousedown', down);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('blur', up);
    };
  }, []);

  return (
    <>
      {bursts.map((b) => (
        <ClickBurst key={b.id} {...b} onDone={remove} />
      ))}
    </>
  );
};
